import BaseApi from "~/api/modules/BaseApi";
import {
  ExportConflict,
  ServiceTrackerExportMode,
  ServiceTrackerExportResult,
} from "./types";

/*
 * Thrown when the backend refuses an export because it conflicts with a
 * previous run and the caller has not confirmed it (kontax-backend#926): an
 * HTTP 409 carrying the conflicts, nothing written. Kept distinct from every
 * other failure — BaseApi's `get` otherwise collapses an axios error down to
 * a generic `Error(message)`, which would lose the conflicts entirely — so
 * the view can show a confirmation instead of the generic error notification.
 */
export class ExportConflictError extends Error {
  public readonly conflicts: ExportConflict[];

  constructor(conflicts: ExportConflict[]) {
    super("The export conflicts with a previous run");
    this.name = "ExportConflictError";
    this.conflicts = conflicts;
  }
}

class ServiceTrackerExportApi extends BaseApi {
  protected get moduleUrl(): string {
    return "/kontax/private/admin";
  }

  /*
   * Preview and export call the SAME endpoint and both regenerate from current
   * data, which is why a correction made in the tracker after previewing ends
   * up in the exported file.
   *
   * `mode` is not a label on an otherwise identical call. It decides two
   * things: whether the run is written to the audit trail, and whether the CSV
   * comes back at all. A preview deliberately withholds the file, so that the
   * bytes cannot be obtained without an attempt to record who took them.
   * Verified against staging: a `mode=preview` response carries `summary`,
   * `plausibility` and `warnings`, and no `csv`.
   *
   * `confirmConflicts` must only ever be sent once the operator has actually
   * confirmed an export the backend flagged as conflicting — it is what turns
   * a would-be 409 into a written, overridden run, so it is only appended to
   * the request when true rather than always sent as `false`.
   */
  public getEasybillExport = async (
    from: string,
    to: string,
    mode: ServiceTrackerExportMode,
    confirmConflicts?: boolean
  ): Promise<ServiceTrackerExportResult> => {
    const { data, status } = await this.get<ServiceTrackerExportResult>(
      "/tax-services/easybill-export",
      {
        params: {
          from,
          to,
          mode,
          ...(confirmConflicts ? { confirmConflicts: true } : {}),
        },
        skipNotification: true,
        /*
         * A conflicting `mode=export` request answers 409 with the conflicts
         * in the body and writes nothing. Accepting it here as a normal
         * response (rather than letting axios treat it as an error) does two
         * things: it keeps the response body reachable instead of losing it
         * to BaseApi's generic `Error(message)` conversion, and it stops the
         * shared axios interceptor from firing an "an error occurred"
         * notification for a state the operator is meant to resolve, not be
         * alarmed by.
         */
        validateStatus: (responseStatus) =>
          responseStatus === 200 || responseStatus === 409,
      }
    );

    if (status === 409) {
      throw new ExportConflictError(data.conflicts ?? []);
    }

    return data;
  };
}

export const serviceTrackerExportApi = new ServiceTrackerExportApi();
